import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Buildings,
  House,
  Circuitry,
  Users,
  Plus,
  Spinner,
  ArrowLeft,
  Trash,
  Link as LinkIcon,
  CurrencyCircleDollar,
  PencilSimple,
  UserPlus,
  ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";
import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCustomerDetailPage = () => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [aptDialog, setAptDialog] = useState(false);
  const [unitDialog, setUnitDialog] = useState(false);
  const [deviceDialog, setDeviceDialog] = useState(false);
  const [assignDeviceDialog, setAssignDeviceDialog] = useState(false);
  const [editCustomerDialog, setEditCustomerDialog] = useState(false);
  const [tenantDialog, setTenantDialog] = useState(false);
  const [assignTenantDialog, setAssignTenantDialog] = useState(false);
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const [permLoading, setPermLoading] = useState(false);
  const [permTemplates, setPermTemplates] = useState([]);
  const [permGroups, setPermGroups] = useState({});
  const [permDefaults, setPermDefaults] = useState({});
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // Form states
  const [aptForm, setAptForm] = useState({ name: "", address: "" });
  const [unitForm, setUnitForm] = useState({ count: 1, prefix: "Daire", start_number: 1 });
  const [deviceForm, setDeviceForm] = useState({ tuya_device_id: "", name: "" });
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", company_name: "", password: "" });
  const [tenantForm, setTenantForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [editTenantDialog, setEditTenantDialog] = useState(false);
  const [editTenantTarget, setEditTenantTarget] = useState(null);
  const [editTenantForm, setEditTenantForm] = useState({ name: "", email: "", phone: "", password: "" });

  useEffect(() => {
    fetchData();
    fetchPermissions();
    if (searchParams.get("edit") === "true") {
      setTimeout(() => openEditCustomerDialog(), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/customers/${customerId}`);
      setData(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const [permRes, templatesRes, groupsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/customers/${customerId}/permissions`),
        axios.get(`${API_URL}/api/admin/permission-templates`),
        axios.get(`${API_URL}/api/admin/permission-groups`),
      ]);
      setPermissions(permRes.data.permissions);
      setSelectedTemplateId(permRes.data.permission_template_id || null);
      setPermTemplates(templatesRes.data);
      setPermGroups(groupsRes.data.groups);
      setPermDefaults(groupsRes.data.defaults);
    } catch { /* ignore */ }
  };

  const handleTogglePermission = async (key, value) => {
    setPermLoading(true);
    try {
      const res = await axios.put(`${API_URL}/api/admin/customers/${customerId}/permissions`, { permissions: { [key]: value } });
      setPermissions(res.data.permissions);
      setSelectedTemplateId(null); // Ozel ayar yapildi, sablon kalkti
      toast.success("Yetki guncellendi");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setPermLoading(false);
    }
  };

  const handleApplyTemplate = async (templateId) => {
    const template = permTemplates.find(t => t.id === templateId);
    if (!template) return;
    setPermLoading(true);
    try {
      const res = await axios.put(`${API_URL}/api/admin/customers/${customerId}/permissions`, {
        permissions: template.permissions,
        template_id: templateId,
      });
      setPermissions(res.data.permissions);
      setSelectedTemplateId(templateId);
      toast.success(`"${template.name}" sablonu uygulandi`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setPermLoading(false);
    }
  };

  const handleCreateApartment = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/customers/${customerId}/apartments`, aptForm);
      toast.success("Apartman olusturuldu");
      setAptDialog(false);
      setAptForm({ name: "", address: "" });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleBulkCreateUnits = async (e) => {
    e.preventDefault();
    if (!selectedApartment) return;
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/apartments/${selectedApartment.id}/units/bulk`, unitForm);
      toast.success(`${unitForm.count} daire olusturuldu`);
      setUnitDialog(false);
      setUnitForm({ count: 1, prefix: "Daire", start_number: 1 });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateDevice = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/customers/${customerId}/devices`, deviceForm);
      toast.success("Sayac eklendi");
      setDeviceDialog(false);
      setDeviceForm({ tuya_device_id: "", name: "" });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleAssignDevice = async (deviceId) => {
    if (!selectedUnit) return;
    setFormLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/units/${selectedUnit.id}/assign-device?device_id=${deviceId}`);
      toast.success("Sayac daireye atandi");
      setAssignDeviceDialog(false);
      setSelectedUnit(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditCustomer = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await axios.put(`${API_URL}/api/admin/customers/${customerId}`, payload);
      toast.success("Musteri bilgileri guncellendi");
      setEditCustomerDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const openEditCustomerDialog = () => {
    const c = data?.customer || {};
    setEditForm({ name: c.name || "", email: c.email || "", phone: c.phone || "", company_name: c.company_name || "", password: "" });
    setEditCustomerDialog(true);
  };

  const handleEditTenant = async (e) => {
    e.preventDefault();
    if (!editTenantTarget) return;
    setFormLoading(true);
    try {
      const payload = { ...editTenantForm };
      if (!payload.password) delete payload.password;
      await axios.put(`${API_URL}/api/tenants/${editTenantTarget.id}`, payload);
      toast.success("Kiraci bilgileri guncellendi");
      setEditTenantDialog(false);
      setEditTenantTarget(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const openEditTenantDialog = (tenant) => {
    setEditTenantTarget(tenant);
    setEditTenantForm({ name: tenant.name || "", email: tenant.email || "", phone: tenant.phone || "", password: "" });
    setEditTenantDialog(true);
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/customers/${customerId}/tenants`, tenantForm);
      toast.success("Kiraci olusturuldu");
      setTenantDialog(false);
      setTenantForm({ name: "", email: "", password: "", phone: "" });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleAssignTenant = async (tenantId) => {
    if (!selectedUnit) return;
    setFormLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/units/${selectedUnit.id}/assign-tenant?tenant_id=${tenantId}`);
      toast.success("Kiraci daireye atandi");
      setAssignTenantDialog(false);
      setSelectedUnit(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={32} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Musteri bulunamadi</p>
        <Link to="/admin">
          <Button className="mt-4" variant="outline">Geri Don</Button>
        </Link>
      </div>
    );
  }

  const { customer, apartments, tenants, devices, tariffs, summary } = data;
  const unassignedDevices = devices.filter(d => !d.unit_id);

  return (
    <div className="space-y-6" data-testid="admin-customer-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin">
          <Button variant="ghost" size="sm" data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-bold text-slate-900">{customer.name}</h1>
          <p className="text-slate-500 text-sm">{customer.email} {customer.company_name && `- ${customer.company_name}`}</p>
        </div>
        <Button variant="outline" onClick={openEditCustomerDialog} data-testid="edit-customer-btn">
          <PencilSimple size={16} className="mr-2" />
          Duzenle
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "Apartman", value: summary.apartments_count, icon: Buildings, bgClass: "bg-indigo-50", textClass: "text-indigo-600" },
          { label: "Daire", value: summary.units_count, icon: House, bgClass: "bg-emerald-50", textClass: "text-emerald-600" },
          { label: "Sayac", value: summary.devices_count, icon: Circuitry, bgClass: "bg-amber-50", textClass: "text-amber-600" },
          { label: "Kiraci", value: summary.tenants_count, icon: Users, bgClass: "bg-purple-50", textClass: "text-purple-600" },
          { label: "Online", value: summary.online_devices, icon: Circuitry, bgClass: "bg-green-50", textClass: "text-green-600" },
        ].map(({ label, value, icon: Icon, bgClass, textClass }) => (
          <Card key={label} className="border-slate-200 shadow-sm">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500">{label}</p>
                <p className="text-2xl font-bold text-slate-900">{value}</p>
              </div>
              <div className={`w-10 h-10 ${bgClass} rounded-xl flex items-center justify-center`}>
                <Icon size={20} weight="duotone" className={textClass} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Permissions Section */}
      {permissions && (
        <Card className="border-slate-200 shadow-sm" data-testid="permissions-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <ShieldCheck size={22} className="text-indigo-600" />
              Musteri Yetkileri
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">Sablon secin veya tek tek yetki duzenleyin</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Template Selector */}
            <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-slate-200">
              <span className="text-sm font-medium text-slate-700 mr-1">Sablon:</span>
              {permTemplates.map(t => (
                <Button key={t.id} size="sm" variant={selectedTemplateId === t.id ? "default" : "outline"}
                  className={`h-8 text-xs ${selectedTemplateId === t.id ? "bg-indigo-600 hover:bg-indigo-700" : ""}`}
                  onClick={() => handleApplyTemplate(t.id)} disabled={permLoading}
                  data-testid={`apply-template-${t.id}`}>
                  {t.name}
                </Button>
              ))}
              {selectedTemplateId && (
                <Badge className="bg-indigo-100 text-indigo-700 text-xs ml-auto">
                  Sablon: {permTemplates.find(t => t.id === selectedTemplateId)?.name}
                </Badge>
              )}
              {!selectedTemplateId && permissions && (
                <Badge className="bg-amber-100 text-amber-700 text-xs ml-auto">Ozel Ayar</Badge>
              )}
              {permTemplates.length === 0 && (
                <span className="text-xs text-slate-400">Henuz sablon yok. Admin {"->"} Yetki Sablonlari sayfasindan olusturun.</span>
              )}
            </div>

            {/* Permission Toggles by Group */}
            {Object.entries(permGroups).map(([groupName, groupPerms]) => (
              <div key={groupName}>
                <p className="text-sm font-semibold text-slate-700 mb-2">{groupName}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {groupPerms.map(({ key, label, desc }) => (
                    <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-testid={`perm-${key}`}>
                      <div className="min-w-0 mr-3">
                        <p className="text-sm font-medium text-slate-900 truncate">{label}</p>
                        <p className="text-[11px] text-slate-500 truncate">{desc}</p>
                      </div>
                      <Switch
                        checked={permissions[key] ?? permDefaults[key] ?? false}
                        onCheckedChange={(val) => handleTogglePermission(key, val)}
                        disabled={permLoading}
                        data-testid={`perm-switch-${key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Apartments Section */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Buildings size={22} className="text-indigo-600" />
            Apartmanlar
          </CardTitle>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => setAptDialog(true)}
            data-testid="add-apartment-btn"
          >
            <Plus size={16} className="mr-2" />
            Apartman Ekle
          </Button>
        </CardHeader>
        <CardContent>
          {apartments.length === 0 ? (
            <div className="text-center py-8">
              <Buildings size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">Henuz apartman yok</p>
            </div>
          ) : (
            <div className="space-y-6">
              {apartments.map((apt) => (
                <div key={apt.id} className="border border-slate-200 rounded-xl p-4" data-testid={`apartment-${apt.id}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-slate-900 text-lg">{apt.name}</h3>
                      {apt.address && <p className="text-sm text-slate-500">{apt.address}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-indigo-100 text-indigo-700">{apt.units_count} Daire</Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedApartment(apt); setUnitDialog(true); }}
                        data-testid={`add-units-btn-${apt.id}`}
                      >
                        <Plus size={14} className="mr-1" />
                        Daire Ekle
                      </Button>
                    </div>
                  </div>

                  {/* Units Table */}
                  {apt.units && apt.units.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>{t("common.unit")}</TableHead>
                            <TableHead>Kat</TableHead>
                            <TableHead>{t("common.meter")}</TableHead>
                            <TableHead>{t("common.tenant")}</TableHead>
                            <TableHead>{t("common.tariff")}</TableHead>
                            <TableHead className="w-24">{t("common.actions")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {apt.units.map((unit) => (
                            <TableRow key={unit.id} data-testid={`unit-row-${unit.id}`}>
                              <TableCell className="font-medium">{unit.name}</TableCell>
                              <TableCell>{unit.floor || "-"}</TableCell>
                              <TableCell>
                                {unit.device_id ? (
                                  <div className="flex items-center gap-2">
                                    <Badge className={unit.device_online ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                                      {unit.device_name || "Sayac"}
                                    </Badge>
                                  </div>
                                ) : (
                                  <span className="text-sm text-slate-400">{t("devices.unassigned")}</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {unit.tenant_name ? (
                                  <span className="text-sm">{unit.tenant_name}</span>
                                ) : (
                                  <span className="text-sm text-slate-400">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {unit.tariff_name ? (
                                  <Badge variant="outline" className="text-xs">{unit.tariff_name}</Badge>
                                ) : (
                                  <span className="text-sm text-slate-400">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  {!unit.device_id && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-indigo-600 hover:text-indigo-700"
                                      onClick={() => { setSelectedUnit(unit); setAssignDeviceDialog(true); }}
                                      data-testid={`assign-device-btn-${unit.id}`}
                                    >
                                      <LinkIcon size={14} className="mr-1" />
                                      Sayac
                                    </Button>
                                  )}
                                  {!unit.tenant_id && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-purple-600 hover:text-purple-700"
                                      onClick={() => { setSelectedUnit(unit); setAssignTenantDialog(true); }}
                                      data-testid={`assign-tenant-btn-${unit.id}`}
                                    >
                                      <UserPlus size={14} className="mr-1" />
                                      Kiraci
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">Daire yok - yukaridaki butonu kullanarak daire ekleyin</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Devices Section */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Circuitry size={22} className="text-amber-600" />
            Sayaclar ({devices.length})
          </CardTitle>
          <Button
            variant="outline"
            onClick={() => setDeviceDialog(true)}
            data-testid="add-device-btn"
          >
            <Plus size={16} className="mr-2" />
            Sayac Ekle
          </Button>
        </CardHeader>
        <CardContent>
          {devices.length === 0 ? (
            <div className="text-center py-8">
              <Circuitry size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">Henuz sayac yok</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {devices.map((device) => (
                <div key={device.id} className="p-3 border border-slate-200 rounded-xl" data-testid={`device-card-${device.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm text-slate-900">{device.name}</span>
                    <Badge className={device.online ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                      {device.online ? "Online" : "Offline"}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-400 mb-1">Seri No: {device.tuya_device_id}</p>
                  <p className="text-xs text-slate-500">
                    {device.unit_id ? "Atanmis" : <span className="text-amber-600 font-medium">{t("devices.unassigned")}</span>}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tenants & Tariffs row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tenants */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Users size={22} className="text-purple-600" />
              Kiracılar ({tenants.length})
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => setTenantDialog(true)} data-testid="add-tenant-btn">
              <Plus size={14} className="mr-1" />
              Kiraci Ekle
            </Button>
          </CardHeader>
          <CardContent>
            {tenants.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">Kiraci yok</p>
            ) : (
              <div className="space-y-2">
                {tenants.map((tn) => (
                  <div key={tn.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-testid={`tenant-card-${tn.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900">{tn.name}</p>
                      <p className="text-xs text-slate-500">{tn.email}{tn.phone ? ` | ${tn.phone}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <Badge className="bg-emerald-100 text-emerald-700">
                        {"\u20ba"}{(tn.balance || 0).toFixed(2)}
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-amber-600 hover:text-amber-700 h-7 w-7 p-0"
                        onClick={() => openEditTenantDialog(tn)}
                        data-testid={`edit-tenant-${tn.id}`}
                        title={t("common.edit")}
                      >
                        <PencilSimple size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tariffs */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <CurrencyCircleDollar size={22} className="text-amber-600" />
              Tarifeler ({tariffs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tariffs.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">Tarife yok</p>
            ) : (
              <div className="space-y-2">
                {tariffs.map((tf) => (
                  <div key={tf.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div>
                      <p className="font-medium text-sm text-slate-900">{tf.name}</p>
                      <p className="text-xs text-slate-500">{tf.tiers?.length} kademe</p>
                    </div>
                    {tf.is_active && <Badge className="bg-indigo-100 text-indigo-700">{t("common.active")}</Badge>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Apartment Dialog */}
      <Dialog open={aptDialog} onOpenChange={setAptDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("apartments.add")}</DialogTitle>
            <DialogDescription>{customer.name} icin yeni apartman</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateApartment}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t("apartments.name")}</Label>
                <Input
                  value={aptForm.name}
                  onChange={(e) => setAptForm({ ...aptForm, name: e.target.value })}
                  placeholder="Orn: Yildiz Apartmani"
                  required
                  data-testid="apt-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("admin.address_label")}</Label>
                <Input
                  value={aptForm.address}
                  onChange={(e) => setAptForm({ ...aptForm, address: e.target.value })}
                  placeholder="Adres"
                  data-testid="apt-address-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAptDialog(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="apt-submit-btn">
                {formLoading ? t("common.creating") : t("common.create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Create Units Dialog */}
      <Dialog open={unitDialog} onOpenChange={setUnitDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("admin.bulk_create_units")}</DialogTitle>
            <DialogDescription>{selectedApartment?.name} icin daireler</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBulkCreateUnits}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t("admin.unit_count")}</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={unitForm.count}
                  onChange={(e) => setUnitForm({ ...unitForm, count: parseInt(e.target.value) || 1 })}
                  required
                  data-testid="unit-count-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t("admin.naming")}</Label>
                  <Input
                    value={unitForm.prefix}
                    onChange={(e) => setUnitForm({ ...unitForm, prefix: e.target.value })}
                    placeholder="Daire"
                    data-testid="unit-prefix-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.start_number")}</Label>
                  <Input
                    type="number"
                    min="1"
                    value={unitForm.start_number}
                    onChange={(e) => setUnitForm({ ...unitForm, start_number: parseInt(e.target.value) || 1 })}
                    data-testid="unit-start-input"
                  />
                </div>
              </div>
              <p className="text-sm text-slate-500">
                Ornek: {unitForm.prefix} {unitForm.start_number}, {unitForm.prefix} {unitForm.start_number + 1}, ... {unitForm.prefix} {unitForm.start_number + (unitForm.count || 1) - 1}
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUnitDialog(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="unit-submit-btn">
                {formLoading ? t("common.creating") : `${unitForm.count} Daire Olustur`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Device Dialog */}
      <Dialog open={deviceDialog} onOpenChange={setDeviceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("admin.add_meter")}</DialogTitle>
            <DialogDescription>{t("devices.add_desc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateDevice}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t("devices.name")}</Label>
                <Input
                  value={deviceForm.name}
                  onChange={(e) => setDeviceForm({ ...deviceForm, name: e.target.value })}
                  placeholder="Orn: Daire 1 Sayaci"
                  required
                  data-testid="device-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("admin.device_serial")}</Label>
                <Input
                  value={deviceForm.tuya_device_id}
                  onChange={(e) => setDeviceForm({ ...deviceForm, tuya_device_id: e.target.value })}
                  placeholder="Cihaz seri numarasi"
                  required
                  data-testid="device-tuya-id-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDeviceDialog(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="device-submit-btn">
                {formLoading ? "Ekleniyor..." : "Sayac Ekle"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Device Dialog */}
      <Dialog open={assignDeviceDialog} onOpenChange={setAssignDeviceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("admin.assign_meter")} - {selectedUnit?.name}</DialogTitle>
            <DialogDescription>Bu daireye atanacak sayaci secin</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {unassignedDevices.length === 0 ? (
              <div className="text-center py-8">
                <Circuitry size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">Atanmamis sayac yok</p>
                <Button
                  size="sm"
                  className="mt-3 bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => { setAssignDeviceDialog(false); setDeviceDialog(true); }}
                >
                  Yeni Sayac Ekle
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {unassignedDevices.map((device) => (
                  <button
                    key={device.id}
                    onClick={() => handleAssignDevice(device.id)}
                    className="w-full flex items-center justify-between p-3 border border-slate-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-left"
                    data-testid={`select-device-${device.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Circuitry size={20} className="text-amber-600" />
                      <div>
                        <p className="font-medium text-slate-900">{device.name}</p>
                        <p className="text-xs text-slate-500">Seri No: {device.tuya_device_id}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={editCustomerDialog} onOpenChange={setEditCustomerDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("admin.edit_customer")}</DialogTitle>
            <DialogDescription>{t("admin.edit_customer_desc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditCustomer}>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Info banner */}
              <div className="p-3 bg-slate-50 rounded-xl text-xs space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Musteri ID:</span><span className="font-mono text-slate-700">{customer.id}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Kayit Tarihi:</span><span className="text-slate-700">{new Date(customer.created_at).toLocaleString("tr-TR")}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Durum:</span><span className={customer.is_active !== false ? "text-emerald-600 font-medium" : "text-red-600 font-medium"}>{customer.is_active !== false ? t("common.active") : t("common.inactive")}</span></div>
              </div>
              <div className="space-y-2">
                <Label>{t("common.name")}</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required data-testid="edit-name-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("common.email")}</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required data-testid="edit-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Yeni Sifre <span className="text-xs text-slate-400">(bos birakirsaniz degismez)</span></Label>
                <Input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Yeni sifre girin..." data-testid="edit-password-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("admin.company_name")}</Label>
                <Input value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} data-testid="edit-company-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("common.phone")}</Label>
                <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-phone-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditCustomerDialog(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="edit-submit-btn">
                {formLoading ? "Kaydediliyor..." : t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Tenant Dialog */}
      <Dialog open={tenantDialog} onOpenChange={setTenantDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("admin.add_tenant")}</DialogTitle>
            <DialogDescription>{customer.name} icin yeni kiraci</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTenant}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t("common.name")}</Label>
                <Input value={tenantForm.name} onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })} required data-testid="tenant-name-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("common.email")}</Label>
                <Input type="email" value={tenantForm.email} onChange={(e) => setTenantForm({ ...tenantForm, email: e.target.value })} required data-testid="tenant-email-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("common.password")}</Label>
                <Input type="password" value={tenantForm.password} onChange={(e) => setTenantForm({ ...tenantForm, password: e.target.value })} required data-testid="tenant-password-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("common.phone")}</Label>
                <Input value={tenantForm.phone} onChange={(e) => setTenantForm({ ...tenantForm, phone: e.target.value })} data-testid="tenant-phone-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTenantDialog(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="tenant-submit-btn">
                {formLoading ? t("common.creating") : t("common.create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Tenant Dialog */}
      <Dialog open={assignTenantDialog} onOpenChange={setAssignTenantDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("admin.assign_tenant")} - {selectedUnit?.name}</DialogTitle>
            <DialogDescription>Bu daireye atanacak kiraciyi secin</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {tenants.length === 0 ? (
              <div className="text-center py-8">
                <Users size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">Henuz kiraci yok</p>
                <Button size="sm" className="mt-3 bg-indigo-600 hover:bg-indigo-700" onClick={() => { setAssignTenantDialog(false); setTenantDialog(true); }}>
                  Kiraci Olustur
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {tenants.map((tn) => (
                  <button
                    key={tn.id}
                    onClick={() => handleAssignTenant(tn.id)}
                    className="w-full flex items-center justify-between p-3 border border-slate-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-colors text-left"
                    data-testid={`select-tenant-${tn.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Users size={20} className="text-purple-600" />
                      <div>
                        <p className="font-medium text-slate-900">{tn.name}</p>
                        <p className="text-xs text-slate-500">{tn.email}</p>
                      </div>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">{"\u20ba"}{(tn.balance || 0).toFixed(2)}</Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog open={editTenantDialog} onOpenChange={setEditTenantDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("admin.edit_tenant")}</DialogTitle>
            <DialogDescription>{editTenantTarget?.name} bilgilerini guncelleyin</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditTenant}>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Info banner */}
              {editTenantTarget && (
                <div className="p-3 bg-slate-50 rounded-xl text-xs space-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Kiraci ID:</span><span className="font-mono text-slate-700">{editTenantTarget.id}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Kayit Tarihi:</span><span className="text-slate-700">{editTenantTarget.created_at ? new Date(editTenantTarget.created_at).toLocaleString("tr-TR") : "-"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Bakiye:</span><span className="font-medium text-emerald-600">{"\u20ba"}{(editTenantTarget.balance || 0).toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Durum:</span><span className={editTenantTarget.blocked ? "text-red-600 font-medium" : "text-emerald-600 font-medium"}>{editTenantTarget.blocked ? "Engelli" : t("common.active")}</span></div>
                </div>
              )}
              <div className="space-y-2">
                <Label>{t("common.name")}</Label>
                <Input value={editTenantForm.name} onChange={(e) => setEditTenantForm({ ...editTenantForm, name: e.target.value })} required data-testid="edit-tenant-name-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("common.email")}</Label>
                <Input type="email" value={editTenantForm.email} onChange={(e) => setEditTenantForm({ ...editTenantForm, email: e.target.value })} required data-testid="edit-tenant-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Yeni Sifre <span className="text-xs text-slate-400">(bos birakirsaniz degismez)</span></Label>
                <Input type="password" value={editTenantForm.password} onChange={(e) => setEditTenantForm({ ...editTenantForm, password: e.target.value })} placeholder="Yeni sifre girin..." data-testid="edit-tenant-password-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("common.phone")}</Label>
                <Input value={editTenantForm.phone} onChange={(e) => setEditTenantForm({ ...editTenantForm, phone: e.target.value })} data-testid="edit-tenant-phone-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditTenantDialog(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="edit-tenant-submit-btn">
                {formLoading ? "Kaydediliyor..." : t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomerDetailPage;
